import React, { useEffect, useMemo, useState } from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import { Error as ErrorComponent } from '../Form';
import API from '../../api/api';
import { auth } from '../../firebase.config';

interface InterestRegistration {
  id: string;
  service: string;
  email: string;
  audience: string;
  createdAt: string | null;
}

class AuthError extends globalThis.Error {
  constructor(message: string) {
    super(message);
    this.name = 'AuthError';
  }
}

const SERVICE_META: Record<string, { label: string; badgeClass: string }> = {
  'animal-genetics': { label: 'Animal Genetics', badgeClass: 'bg-success' },
  'human-genetics': { label: 'Human Genetics', badgeClass: 'bg-primary' },
  igisekuruza: { label: 'iGiSEKURUZA', badgeClass: 'bg-warning text-dark' },
};

const AUDIENCE_LABELS: Record<string, string> = {
  farmer: 'Farmer / Livestock Breeder',
  vet: 'Veterinary Professional',
  agribusiness: 'Agribusiness / Research Partner',
  individual: 'Individual',
  provider: 'Healthcare Provider',
  research: 'Research Partner',
};

const formatAudience = (value: string) =>
  AUDIENCE_LABELS[value] || value.charAt(0).toUpperCase() + value.slice(1);

const formatService = (value: string) =>
  SERVICE_META[value] || { label: value, badgeClass: 'bg-secondary' };

const getAuthToken = () => {
  if (auth.currentUser) {
    return auth.currentUser.getIdToken(true).then((token) => {
      localStorage.setItem('token', JSON.stringify(token));
      return token;
    });
  }

  const storedToken = localStorage.getItem('token');
  if (storedToken) {
    try {
      return Promise.resolve(JSON.parse(storedToken));
    } catch (e) {
      return Promise.resolve(storedToken.replace(/^"|"$/g, ''));
    }
  }

  return Promise.reject(new AuthError('No authentication token available'));
};

const escapeCsvValue = (value: string) => `"${value.replace(/"/g, '""')}"`;

const ServiceInterestList: React.FC = () => {
  const [registrations, setRegistrations] = useState<InterestRegistration[]>([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [serviceFilter, setServiceFilter] = useState<string>('all');

  useEffect(() => {
    fetchRegistrations();
    // Mount-only fetch
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRegistrations = async () => {
    setIsRefreshing(true);
    setError(null);

    try {
      const token = await getAuthToken();
      const response = await API.get('/service-interest', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRegistrations(response.data.data || []);
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          "Failed to load interest registrations. Please check your connection and try again."
      );
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  const servicesPresent = useMemo(() => {
    const unique = new Set(registrations.map((r) => r.service));
    return Array.from(unique);
  }, [registrations]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return registrations.filter((r) => {
      const matchesService = serviceFilter === 'all' || r.service === serviceFilter;
      const matchesSearch = !term || r.email.toLowerCase().includes(term);
      return matchesService && matchesSearch;
    });
  }, [registrations, search, serviceFilter]);

  const stats = useMemo(() => {
    const now = Date.now();
    const sevenDaysAgo = now - 7 * 24 * 60 * 60 * 1000;
    const byService: Record<string, number> = {};
    let recentCount = 0;

    registrations.forEach((r) => {
      byService[r.service] = (byService[r.service] || 0) + 1;
      if (r.createdAt && new Date(r.createdAt).getTime() >= sevenDaysAgo) {
        recentCount += 1;
      }
    });

    return { total: registrations.length, byService, recentCount };
  }, [registrations]);

  const handleExportCsv = () => {
    const header = ['Email', 'Experience', 'Audience', 'Registered At'];
    const rows = filtered.map((r) => [
      r.email,
      formatService(r.service).label,
      formatAudience(r.audience),
      r.createdAt ? new Date(r.createdAt).toISOString() : '',
    ]);

    const csv = [header, ...rows]
      .map((row) => row.map(escapeCsvValue).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `service-interest-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {error && <ErrorComponent>{error}</ErrorComponent>}

      {/* Stats Cards */}
      <div className="row mb-4 g-3">
        <div className="col-md-3 col-sm-6">
          <div className="card h-100 border">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="card-title text-muted mb-0 text-uppercase small">Total Signups</h6>
                <span className="badge bg-opacity-10 text-primary p-2">
                  <i className="fas fa-users"></i>
                </span>
              </div>
              <h2 className="fw-bold mb-1">{stats.total}</h2>
            </div>
          </div>
        </div>

        <div className="col-md-3 col-sm-6">
          <div className="card h-100 border">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="card-title text-muted mb-0 text-uppercase small">Animal Genetics</h6>
                <span className="badge bg-success bg-opacity-10 text-success p-2">
                  <i className="fas fa-paw"></i>
                </span>
              </div>
              <h2 className="fw-bold mb-1">{stats.byService['animal-genetics'] || 0}</h2>
            </div>
          </div>
        </div>

        <div className="col-md-3 col-sm-6">
          <div className="card h-100 border">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="card-title text-muted mb-0 text-uppercase small">Human Genetics</h6>
                <span className="badge bg-primary bg-opacity-10 text-primary p-2">
                  <i className="fas fa-dna"></i>
                </span>
              </div>
              <h2 className="fw-bold mb-1">{stats.byService['human-genetics'] || 0}</h2>
            </div>
          </div>
        </div>

        <div className="col-md-3 col-sm-6">
          <div className="card h-100 border">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="card-title text-muted mb-0 text-uppercase small">New This Week</h6>
                <span className="badge bg-info bg-opacity-10 text-info p-2">
                  <i className="fas fa-clock"></i>
                </span>
              </div>
              <h2 className="fw-bold mb-1">{stats.recentCount}</h2>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-3">
          <button className="btn btn-primary" onClick={fetchRegistrations} disabled={isRefreshing}>
            {isRefreshing ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Retrying...
              </>
            ) : (
              <>
                <i className="fas fa-sync-alt me-2"></i>
                Retry
              </>
            )}
          </button>
        </div>
      )}

      <div className="card">
        <div className="card-header bg-white d-flex flex-wrap align-items-center justify-content-between gap-2">
          <h5 className="mb-0">Interest Registrations</h5>
          <div className="d-flex flex-wrap gap-2">
            <input
              type="search"
              className="form-control form-control-sm"
              placeholder="Search by email"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ minWidth: '200px' }}
            />
            <select
              className="form-select form-select-sm"
              value={serviceFilter}
              onChange={(e) => setServiceFilter(e.target.value)}
              style={{ minWidth: '160px' }}
            >
              <option value="all">All experiences</option>
              {servicesPresent.map((service) => (
                <option key={service} value={service}>
                  {formatService(service).label}
                </option>
              ))}
            </select>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={fetchRegistrations}
              disabled={isRefreshing}
              title="Refresh"
            >
              <i className={`fas fa-sync-alt ${isRefreshing ? 'fa-spin' : ''}`}></i>
            </button>
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={handleExportCsv}
              disabled={filtered.length === 0}
            >
              <i className="fas fa-download me-1"></i>
              Export CSV
            </button>
          </div>
        </div>

        <div className="card-body p-0">
          {filtered.length === 0 ? (
            <div className="text-center text-muted py-5">
              <i className="fas fa-inbox fa-2x mb-3 d-block"></i>
              {registrations.length === 0
                ? 'No one has registered interest yet.'
                : 'No registrations match your search or filter.'}
            </div>
          ) : (
            <div
              className="table-responsive"
              style={{ height: '60vh', overflowY: 'auto', overflowX: 'auto' }}
            >
              <table className="table table-hover mb-0" style={{ width: 'auto', minWidth: '100%' }}>
                <thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
                  <tr>
                    <th style={{ whiteSpace: 'nowrap', paddingRight: '20px' }}>Email</th>
                    <th style={{ whiteSpace: 'nowrap', paddingRight: '20px' }}>Experience</th>
                    <th style={{ whiteSpace: 'nowrap', paddingRight: '20px' }}>I'm a...</th>
                    <th style={{ whiteSpace: 'nowrap', paddingRight: '20px' }}>Registered</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((registration) => {
                    const serviceMeta = formatService(registration.service);
                    return (
                      <tr key={registration.id}>
                        <td style={{ whiteSpace: 'nowrap', paddingRight: '20px' }}>{registration.email}</td>
                        <td style={{ whiteSpace: 'nowrap', paddingRight: '20px' }}>
                          <span className={`badge ${serviceMeta.badgeClass}`}>{serviceMeta.label}</span>
                        </td>
                        <td style={{ whiteSpace: 'nowrap', paddingRight: '20px' }}>
                          {formatAudience(registration.audience)}
                        </td>
                        <td style={{ whiteSpace: 'nowrap', paddingRight: '20px' }}>
                          {registration.createdAt ? (
                            <span title={format(new Date(registration.createdAt), 'PPpp')}>
                              {formatDistanceToNow(new Date(registration.createdAt), { addSuffix: true })}
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ServiceInterestList;
